import React from 'react';
import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, TextField, Box } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { DEFAULT_REQUESTED_SAMPLING, REQUESTED_SAMPLING_LABELS, RequestedSampling } from './requestedSampling';

interface RequestedSamplingFieldProps {
    value: RequestedSampling;
    onChange: (next: RequestedSampling) => void;
}

/**
 * SHER-1043 — optional "Date/heure souhaitée" on the laboratory request form.
 * Defaults to Routine so routine prescribing stays one click.
 */
const RequestedSamplingField: React.FC<RequestedSamplingFieldProps> = ({
    value = DEFAULT_REQUESTED_SAMPLING,
    onChange,
}) => {
    const kind = value.kind;
    const datetimeLocal = value.kind === 'datetime'
        ? toDatetimeLocalValue(value.at)
        : '';

    return (
        <FormControl component="fieldset" data-testid="requested-sampling-field" sx={{ mt: 2, mb: 1 }}>
            <FormLabel component="legend">
                <Translate id={REQUESTED_SAMPLING_LABELS.legend} />
            </FormLabel>
            <RadioGroup
                value={kind}
                onChange={(event) => {
                    const next = event.target.value;
                    if (next === 'routine') {
                        onChange({ kind: 'routine' });
                    } else if (next === 'urgent') {
                        onChange({ kind: 'urgent' });
                    } else {
                        onChange({
                            kind: 'datetime',
                            at: value.kind === 'datetime' ? value.at : new Date().toISOString(),
                        });
                    }
                }}
            >
                <FormControlLabel
                    value="routine"
                    control={<Radio data-testid="requested-sampling-routine" />}
                    label={<Translate id={REQUESTED_SAMPLING_LABELS.routine} />}
                />
                <FormControlLabel
                    value="urgent"
                    control={<Radio data-testid="requested-sampling-urgent" />}
                    label={<Translate id={REQUESTED_SAMPLING_LABELS.urgent} />}
                />
                <FormControlLabel
                    value="datetime"
                    control={<Radio data-testid="requested-sampling-datetime" />}
                    label={<Translate id={REQUESTED_SAMPLING_LABELS.datetime} />}
                />
            </RadioGroup>
            {kind === 'datetime' && (
                <Box sx={{ mt: 1, maxWidth: 320 }}>
                    <TextField
                        type="datetime-local"
                        fullWidth
                        size="small"
                        value={datetimeLocal}
                        onChange={(event) => {
                            const iso = fromDatetimeLocalValue(event.target.value);
                            if (iso) {
                                onChange({ kind: 'datetime', at: iso });
                            }
                        }}
                        inputProps={{ 'data-testid': 'requested-sampling-datetime-input' }}
                    />
                </Box>
            )}
        </FormControl>
    );
};

function toDatetimeLocalValue(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromDatetimeLocalValue(local: string): string | null {
    if (!local) {
        return null;
    }
    const date = new Date(local);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    return date.toISOString();
}

export default RequestedSamplingField;
